import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { api, errMsg, fmtDate, duration } from "@/lib/api";

export default function AdminResults() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const testId = params.get("test") || "all";
  const studentId = params.get("student") || "all";
  const [rows, setRows] = useState([]);
  const [tests, setTests] = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    api.get("/admin/tests").then((r) => setTests(r.data));
    api.get("/admin/students").then((r) => setStudents(r.data));
  }, []);

  const load = () => {
    const p = {};
    if (testId !== "all") p.test_id = testId;
    if (studentId !== "all") p.student_id = studentId;
    api.get("/admin/results", { params: p }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [testId, studentId]);

  const setFilter = (k, v) => { const n = new URLSearchParams(params); if (v === "all") n.delete(k); else n.set(k, v); setParams(n); };
  const remove = async (a) => {
    try { await api.delete(`/admin/results/${a.id}`); toast.success("Result deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="small-label">Control Panel</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Student Results</h1>
      <div className="flex flex-wrap gap-3 mt-8">
        <Select value={testId} onValueChange={(v) => setFilter("test", v)}>
          <SelectTrigger className="w-60 bg-white" data-testid="results-filter-test"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All tests</SelectItem>{tests.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={studentId} onValueChange={(v) => setFilter("student", v)}>
          <SelectTrigger className="w-60 bg-white" data-testid="results-filter-student"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All students</SelectItem>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="admin-results-table">
          <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Test</TableHead><TableHead>Score</TableHead><TableHead>Result</TableHead>
            <TableHead>Time</TableHead><TableHead>Submitted</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No results yet.</TableCell></TableRow>}
            {rows.map((a) => (
              <TableRow key={a.id} data-testid={`admin-result-row-${a.id}`}>
                <TableCell><div className="font-medium">{a.student_name}</div><div className="text-xs text-slate-500">{a.student_email}</div></TableCell>
                <TableCell>{a.test_title}</TableCell>
                <TableCell className="font-mono">{a.score}/{a.total} · {Math.round(a.percentage)}%</TableCell>
                <TableCell><Badge className={a.passed ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-800 hover:bg-red-100"}>{a.passed ? "Passed" : "Failed"}</Badge></TableCell>
                <TableCell className="text-slate-500">{duration(a.started_at, a.submitted_at)}</TableCell>
                <TableCell className="text-slate-500">{fmtDate(a.submitted_at)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => nav(`/admin/results/${a.id}`)} data-testid={`admin-view-result-${a.id}`}><Eye className="h-4 w-4" /></Button>
                  <ConfirmDelete testId={`admin-delete-result-${a.id}`} title="Delete result?" description="The student will no longer see this attempt." onConfirm={() => remove(a)}
                    trigger={<Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-result-${a.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
